import { graphql, PageProps } from "gatsby";
import React, { useEffect, useState } from "react";
import { VarzenalQuery } from "../../../../graphql-types";
import { default as GlobalHead } from "../../../components/head";
import Layout from "../../../components/layout";
import { GatsbyImage } from "gatsby-plugin-image";
import YouTubeEmbed from "../../../components/YouTubeEmbed";
import { AltCarousel } from "../../../components/Carousel";

import kondzillaIcon from "../../../images/omo/br/varzenal/partners/kondzilla.svg";
import omoArsenalIcon from "../../../images/omo/br/varzenal/partners/omoArsenal.svg";

import soccer from "../../../images/omo/br/varzenal/episodes/soccer.svg";
import dog from "../../../images/omo/br/varzenal/project/dog.svg";
import grill from "../../../images/omo/br/varzenal/partners/grill.svg";
import chair from "../../../images/omo/br/varzenal/teams/chair.svg";
import heroLogo from "../../../images/omo/br/varzenal/menu/heroLogo.svg";

import aclimacaoBadge from "../../../images/omo/br/varzenal/teams/carousel/badges/Aclimacao.svg";
import embuGuacuBadge from "../../../images/omo/br/varzenal/teams/carousel/badges/EmbuGuacu.svg";
import guarulhosBadge from "../../../images/omo/br/varzenal/teams/carousel/badges/Guardulhos.svg";
import ipirangaBadge from "../../../images/omo/br/varzenal/teams/carousel/badges/Ipiranga.svg";
import jardimSeniceBadge from "../../../images/omo/br/varzenal/teams/carousel/badges/Jardim.svg";
import raizBadge from "../../../images/omo/br/varzenal/teams/carousel/badges/Raiz.svg";
import tiradentesBadge from "../../../images/omo/br/varzenal/teams/carousel/badges/Cid.svg";
import vilaSuicaBadge from "../../../images/omo/br/varzenal/teams/carousel/badges/VilaSuica.svg";

import omoArsenalIconBlue from "../../../images/omo/br/varzenal/partners/omoArsenalBlue.svg";
import kondzillaIconBlue from "../../../images/omo/br/varzenal/partners/kondzillaBlue.svg";
// import omnicomIcon from "../../../images/omo/br/varzenal/partners/omnicom.svg";
import rbIcon from "../../../images/omo/br/varzenal/partners/rb.svg";
// import wassermanIcon from "../../../images/omo/br/varzenal/partners/wasserman.svg";

import "../../../css/varzenal-page.css";

type Props = Pick<PageProps<VarzenalQuery>, "data">;

const href = "https://www.omo.com/br/varzenal.html";
const title = "VARZENAL";
const description =
  "VARZENAL - OMO and KondZilla present grassroots football in São Paulo";

type Team = {
  title: string;
  text: string;
};

type Episode = {
  title: string;
  text: string;
  youtubeId: string;
};

type Translation = {
  nav: {
    project: string;
    episodes: string;
    teams: string;
  };
  presents: string;
  projectHeading: string;
  projectParagraphs: string[];
  episodesHeading: string;
  episodes: Episode[];
  teamsHeading: string;
  teams: Team[];
  partnersHeading: string;
  partnersCoCreated: string;
};

type Translations = {
  [lang: string]: Translation;
};

const translations: Translations = {
  en: {
    nav: {
      project: "Project",
      episodes: "Episodes",
      teams: "Teams",
    },
    presents: "Presents",
    projectHeading: "OMO Varzenal Cup",
    projectParagraphs: [
      "Follow the story of the OMO Varzenal Cup",
      "8 grassroots varzea teams from Brazil competing for the chance to bring their story to London.",
      "Football in its purest form.",
    ],
    episodesHeading: "Episodes",
    episodes: [
      {
        title: "Episódio 1 - A Várzea",
        text: "Get to know a bit of the history and the influence of grassroots football.",
        youtubeId: "RocawTwkzsc",
      },
      {
        title: "Episódio 2 - A Copa",
        text: "The cup kicks off in Santo André with the first round.",
        youtubeId: "KV692sQaSLc",
      },
      {
        title: "Episódio 3 - As Semifinais",
        text: "The teams face off for a shot at the big final.",
        youtubeId: "gFO1sv57IkE",
      },
      {
        title: "Episódio 4 - Welcome to London",
        text: "All the impressions from the trip through the players’ perspective.",
        youtubeId: "DV8zGDHtqOw",
      },
      {
        title: "Episódio 5 - A Grande Final",
        text: "The big moment. The very first international final for brazilian grassroots football.",
        youtubeId: "BJ9FMwDAzl0",
      },
    ],

    teamsHeading: "The Teams",
    teams: [
      {
        title: "Arsenal Aclimação",
        text: "Bound by the will to win, the team from central São Paulo dreams of making it to the final in London.",
      },
      {
        title: "Arsenal Jardim Senice",
        text: "Founded in 1991 by Mr. Augusto, the team has already racked up plenty of titles in the várzea.",
      },
      {
        title: "Arsenal Raíz (Jaçanã)",
        text: "Off the pitch, the team works on social projects in the community. And for every match, they’ve got the unconditional support of their fans.",
      },
      {
        title: "Arsenal de Guarulhos",
        text: "Established in 1968, it started out as a neighborhood friends’ team and grew into one of the biggest grassroots teams in Brazil.",
      },
      {
        title: "Arsenal Vila Suíça",
        text: "Straight from São Paulo’s East Side, the team founded in 2004 hits the pitch with grit and full support of their community.",
      },
      {
        title: "Arsenal Embu-Guaçu",
        text: "A team that’s basically a family! No matter the result, they know they can always count on each other, on and off the pitch.",
      },
      {
        title: "Arsenal Tiradentes",
        text: "Directly inspired by the English club, the East Side team comes into the cup at full strength.",
      },
      {
        title: "Arsenal Ipiranga",
        text: "The team was born out of the friendship between five school friends who came together to start a football team.",
      },
    ],
    partnersHeading: "Partners",
    partnersCoCreated: "Co-Created with",
  },
  pt: {
    nav: {
      project: "Projeto",
      episodes: "Episódios",
      teams: "Times",
    },
    presents: "Apresentam",
    projectHeading: "A Copa OMO Varzenal",
    projectParagraphs: [
      "Acompanhe a história de OMO Varzenal",
      "8 times de várzea brasileiros competindo pela chance de levar sua história para Londres.",
      "O futebol em sua forma mais pura.",
    ],
    episodesHeading: "Episódios",
    episodes: [
      {
        title: "Episódio 1 - A Várzea",
        text: "Conheça um pouco da história e da influência do futebol de várzea.",
        youtubeId: "RocawTwkzsc",
      },
      {
        title: "Episódio 2 - A Copa",
        text: "Rola a bola em Santo André na primeira fase da copa.",
        youtubeId: "KV692sQaSLc",
      },
      {
        title: "Episódio 3 - As Semifinais",
        text: "Os times se enfrentam para chegar na grande final.",
        youtubeId: "gFO1sv57IkE",
      },
      {
        title: "Episódio 4 - Welcome to London",
        text: "Todas as impressões da viagem pelo olhar dos jogadores.",
        youtubeId: "DV8zGDHtqOw",
      },
      {
        title: "Episódio 5 - A Grande Final",
        text: "O momento mais importante. A primeira final internacional do futebol de várzea.",
        youtubeId: "BJ9FMwDAzl0",
      },
    ],

    teamsHeading: "Os Times",
    teams: [
      {
        title: "Arsenal Aclimação",
        text: "Unidos pela vontade de ganhar, o time da região central de São Paulo sonha com a final em Londres.",
      },
      {
        title: "Arsenal Jardim Senice",
        text: "O time fundado em 1991, pelo Sr. Augusto, já conquistou diversos títulos na várzea.",
      },
      {
        title: "Arsenal Raíz (Jaçanã)",
        text: "Time que, fora das quatro linhas, atua em projetos sociais na comunidade. Conta com o apoio incondicional da sua torcida em cada jogo.",
      },
      {
        title: "Arsenal de Guarulhos",
        text: "Formado em 1968, começou como um time de amigos do bairro e se transformou em um dos maiores times da várzea brasileira.",
      },
      {
        title: "Arsenal Vila Suíça",
        text: "Diretamente da zona leste de São Paulo, o time formado em 2004 entra em campo com raça e com o apoio da comunidade.",
      },
      {
        title: "Arsenal Embu-Guaçu",
        text: "Um time que é uma família! Independente do resultado, todos podem sempre contar uns com os outros, dentro e fora de campo.",
      },
      {
        title: "Arsenal Tiradentes",
        text: "Diretamente inspirados pelo time inglês, o time da zona leste chega com força total para a copa.",
      },
      {
        title: "Arsenal Ipiranga",
        text: "A equipe nasce da amizade entre 5 amigos de colégio que se uniram para formar um time de futebol.",
      },
    ],

    partnersHeading: "Parceiros",
    partnersCoCreated: "Cocriado com",
  },
};

export default function VarzenalPage({ data }: Props) {
  if (!data.website) {
    throw new Error("Could not find website node");
  }

  const [lang, setLang] = useState("en");

  useEffect(() => {
    if (typeof window !== "undefined") {
      const storedLang = localStorage.getItem("varzenal-lang");
      if (storedLang === "en" || storedLang === "pt") {
        setLang(storedLang);
        return;
      }

      const detected =
        (navigator.languages && navigator.languages[0]) ||
        navigator.language ||
        "";
      if (detected.toLowerCase().startsWith("en")) {
        setLang("en");
      } else if (detected.toLowerCase().startsWith("pt")) {
        setLang("pt");
      } else {
        setLang("en");
      }
    }
  }, []);

  const teamImages = [
    {
      shield: aclimacaoBadge,
      team: data.aclimacao?.childImageSharp?.gatsbyImageData,
    },
    {
      shield: jardimSeniceBadge,
      team: data.jardimSenice?.childImageSharp?.gatsbyImageData,
    },
    {
      shield: raizBadge,
      team: data.raiz?.childImageSharp?.gatsbyImageData,
    },
    {
      shield: guarulhosBadge,
      team: data.guarulhos?.childImageSharp?.gatsbyImageData,
    },
    {
      shield: vilaSuicaBadge,
      team: data.vilaSuica?.childImageSharp?.gatsbyImageData,
    },

    {
      shield: embuGuacuBadge,
      team: data.embuGuacu?.childImageSharp?.gatsbyImageData,
    },

    {
      shield: tiradentesBadge,
      team: data.tiradentes?.childImageSharp?.gatsbyImageData,
    },
    {
      shield: ipirangaBadge,
      team: data.ipiranga?.childImageSharp?.gatsbyImageData,
    },
  ];
  return (
    <Layout
      absolutePath={href}
      home={data.website}
      metaDescription={description}
      pageTitle={title}
      hideShell
      fullWidth
      className="bg-[#F5F1E8] min-h-screen scroll-smooth"
    >
      <div className="font-helveticaNeue  max-w-7xl mx-auto">
        {/* Navigation */}
        <nav className="sticky top-0 z-50 bg-[#F5F1E8] border-b border-red-500/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <div className="font-helveticaLTStdCondensed flex items-center justify-between sm:gap-8 pt-3 pb-1 w-full flex-nowrap ">
              <a
                href="#project"
                className="flex-1 text-center text-[#DA2D2F] uppercase font-black text-2xl sm:text-3xl hover:underline"
              >
                {translations[lang].nav.project}
              </a>
              <a
                href="#episodes"
                className="flex-1 text-center text-[#DA2D2F] uppercase font-black text-2xl sm:text-3xl hover:underline"
              >
                {translations[lang].nav.episodes}
              </a>
              <a
                href="#teams"
                className="flex-1 text-center text-[#DA2D2F] uppercase font-black text-2xl sm:text-3xl hover:underline"
              >
                {translations[lang].nav.teams}
              </a>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="relative w-full flex flex-col items-center justify-center max-w-7xl mx-auto pb-[2%]">
          <div className="absolute inset-0">
            <GatsbyImage
              image={data.heroImage?.childImageSharp?.gatsbyImageData}
              alt="Hero"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="grid grid-cols-2 gap-8 sm:gap-12 z-10 mt-4 w-1/2 sm:w-auto h-1/2">
            <img
              alt="Omo Arsenal"
              src={omoArsenalIcon}
              className="w-full h-full object-contain"
            />
            <img
              alt="Kondzilla"
              src={kondzillaIcon}
              className="w-full h-full object-contain"
            />
          </div>
          <img
            alt="Dirt is Glory"
            src={heroLogo}
            className="w-3/4 h-1/2 object-contain my-18 mb-24 z-10"
          />
        </section>

        {/* THE PROJECT Section */}
        <section id="project" className="pt-12 pb-0">
          <div className="relative">
            <div className="flex flex-col lg:flex-row items-start gap-8 lg:gap-12 justify-center">
              <div className="flex-1 px-6">
                <h2 className="font-helveticaLTStdCondensed text-5xl sm:text-6xl lg:text-7xl font-bold text-[#DA2D2F] uppercase mb-3 text-center">
                  {translations[lang].projectHeading}
                </h2>

                <div className="">
                  <div className="space-y-4 text-sm sm:text-base lg:text-lg text-[#DA2D2F] text-center py-4">
                    {translations[lang].projectParagraphs.map((paragraph) => (
                      <p>{paragraph}</p>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="relative py-[15%]">
          <div className="w-full">
            <GatsbyImage
              image={data.pictures?.childImageSharp?.gatsbyImageData}
              alt="Pictures"
              className="w-full object-contain"
            />
          </div>
          <div className="absolute top-0 left-0 w-full flex flex-col z-10 h-full">
            {Array.from({ length: 3 }).map((_, i) => (
              <img
                key={i}
                alt="Dog"
                src={dog}
                className="h-1/3 object-contain object-right"
              />
            ))}
          </div>
        </div>

        <div className="relative py-[15%] -my-[10%]">
          <div className="w-full ">
            <GatsbyImage
              image={data.lockerRoomImage?.childImageSharp?.gatsbyImageData}
              alt="Locker Room"
              className="w-full h-full object-contain"
            />
          </div>
          <div className="absolute top-0 left-0 w-full flex flex-col z-10 h-full">
            {Array.from({ length: 3 }).map((_, i) => (
              <img
                key={i}
                alt="Soccer"
                src={soccer}
                className="h-1/3 object-contain object-left"
              />
            ))}
          </div>
        </div>

        {/* EPISODES Section */}
        <section id="episodes" className="py-12 sm:py-16 lg:py-24">
          <div className="relative">
            <div className="">
              <div className="flex-col lg:flex-row items-start gap-8 lg:gap-12">
                <h2 className="font-helveticaLTStdCondensed text-5xl sm:text-6xl lg:text-7xl font-bold text-[#013475] uppercase mb-6 text-center">
                  {translations[lang].episodesHeading}
                </h2>
                <div className="mx-3 flex flex-col gap-8">
                  {translations[lang].episodes.map((episode) => (
                    <div className="w-full px-3">
                      <div className="w-3/4">
                        <h3 className="text-sm font-bold text-[#013475] uppercase">
                          {episode.title}
                        </h3>
                        <p className="text-[#013475] text-sm pb-1 uppercase">
                          {episode.text}
                        </p>
                      </div>

                      <div className="relative mx-auto">
                        <div className="absolute top-0 right-0 -mt-8 -mr-3">
                          <GatsbyImage
                            image={
                              data.blueStar?.childImageSharp?.gatsbyImageData
                            }
                            alt="Blue Stars"
                            className=" w-14 h-14 "
                          />
                        </div>

                        <YouTubeEmbed
                          id={episode.youtubeId}
                          title="CORRA E GANHE! OMO Ciclo Rápido."
                          width="100%"
                          className="aspect-video"
                          turnOnCaptions={true}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="relative py-[15%] -my-[10%]">
          <div className="w-full ">
            <GatsbyImage
              image={data.player?.childImageSharp?.gatsbyImageData}
              alt="Player"
              className="w-full h-full object-contain"
            />
          </div>
          <div className="absolute top-0 left-0 w-full flex flex-col z-10 h-full">
            {Array.from({ length: 3 }).map((_, i) => (
              <img
                key={i}
                alt="Chair"
                src={chair}
                className="h-1/3 object-contain object-right"
              />
            ))}
          </div>
        </div>

        {/* THE TEAMS Section */}
        <section id="teams" className="py-12 sm:py-16 lg:py-24">
          <div className="relative">
            <div className="">
              <div className="flex-col lg:flex-row items-start gap-8 lg:gap-12">
                <h2 className="font-helveticaLTStdCondensed text-5xl sm:text-6xl lg:text-7xl font-bold text-[#DA2D2F] uppercase mb-6 text-center">
                  {translations[lang].teamsHeading}
                </h2>
                <div>
                  <AltCarousel name="teams" color="#DA2D2F">
                    {translations[lang].teams.map((team, index) => (
                      <div className="w-full">
                        <div className="flex items-center gap-8 my-2 h-[170px] sm:h-[150px] lg:h-[175px] px-4">
                          <img
                            src={teamImages[index].shield}
                            alt="Shield"
                            className="w-1/4 sm:w-32"
                          />
                          <div className="text-[#DA2D2F]">
                            <h3 className="text-sm font-bold uppercase">
                              {team.title}
                            </h3>
                            <p className="text-sm pb-3 uppercase">
                              {team.text}
                            </p>
                          </div>
                        </div>

                        <div className="mx-auto px-4">
                          <GatsbyImage
                            image={teamImages[index].team}
                            alt="Team"
                            className=""
                            objectFit="contain"
                          />
                        </div>
                      </div>
                    ))}
                  </AltCarousel>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="relative py-[15%] -my-[5%]">
          <div className="w-full ">
            <GatsbyImage
              image={data.crowd?.childImageSharp?.gatsbyImageData}
              alt="Crowd"
              className="w-full object-contain"
            />
          </div>
          <div className="absolute top-0 left-0 w-full flex flex-col z-10 h-full">
            {Array.from({ length: 3 }).map((_, i) => (
              <img
                key={i}
                alt="Grill"
                src={grill}
                className="h-1/3 object-contain object-left"
              />
            ))}
          </div>
        </div>

        {/* PARTNERS Section */}
        <section
          id="partners"
          className="py-12 sm:py-16 lg:py-24 px-4 sm:px-6 lg:px-8"
        >
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col items-center justify-center gap-4">
              <h2 className="font-helveticaLTStdCondensed text-5xl sm:text-6xl lg:text-7xl font-bold text-[#013475] uppercase text-center mb-6">
                {translations[lang].partnersHeading}
              </h2>

              <img alt="omo arsenal icon" src={omoArsenalIconBlue} />
              <p className="text-lg font-bold uppercase text-[#013475] my-6">
                {" "}
                {translations[lang].partnersCoCreated}{" "}
              </p>

              {/* Partner Logos Grid */}
              <div className="grid grid-cols-2 gap-8 max-w-2xl justify-center items-center justify-items-center">
                {/* <img alt="omnicom icon" src={omnicomIcon} /> */}
                <img alt="kondzilla icon" src={kondzillaIconBlue} />
                {/* <img alt="wassermann icon" src={wassermanIcon} /> */}
                <img alt="rb icon" src={rbIcon} />
                <div className="text-sm font-semibold uppercase text-[#013475]">
                  Ian Wright
                </div>
                <div className="text-sm font-semibold uppercase text-[#013475]">
                  Gilberto Silva
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Language Switcher */}
        <div className="flex flex items-center justify-center gap-2 px-3 py-2">
          {/* Portugal (pt) */}
          <button
            aria-label="Switch to Portuguese"
            className={`focus:outline-none transition-all duration-150 border-2 text-3xl ${
              lang === "pt" ? "border-[#DA2D2F]" : "border-transparent"
            } rounded-full w-8 h-8 flex items-center justify-center`}
            onClick={() => {
              if (typeof window !== "undefined") {
                setLang("pt");
                localStorage.setItem("varzenal-lang", "pt");
              }
            }}
          >
            🇧🇷
          </button>
          {/* English (en) */}
          <button
            aria-label="Switch to English"
            className={`focus:outline-none transition-all duration-150 border-2 text-3xl ${
              lang === "en" ? "border-[#DA2D2F]" : "border-transparent"
            } rounded-full w-8 h-8 flex items-center justify-center`}
            onClick={() => {
              if (typeof window !== "undefined") {
                localStorage.setItem("varzenal-lang", "en");
                setLang("en");
              }
            }}
          >
            🇬🇧
          </button>
        </div>
      </div>
    </Layout>
  );
}

export const query = graphql`
  query Varzenal {
    website {
      ...homeFields
    }
    star: file(relativePath: { eq: "omo/br/varzenal/menu/star.png" }) {
      childImageSharp {
        gatsbyImageData
      }
    }
    heroImage: file(relativePath: { eq: "omo/br/varzenal/menu/photo1.png" }) {
      childImageSharp {
        gatsbyImageData
      }
    }
    brandsLogo: file(relativePath: { eq: "omo/br/varzenal/menu/brands.png" }) {
      childImageSharp {
        gatsbyImageData
      }
    }
    varzenalLogo: file(
      relativePath: { eq: "omo/br/varzenal/menu/varzenal.png" }
    ) {
      childImageSharp {
        gatsbyImageData
      }
    }
    pictures: file(
      relativePath: { eq: "omo/br/varzenal/project/pictures.png" }
    ) {
      childImageSharp {
        gatsbyImageData
      }
    }
    dog: file(relativePath: { eq: "omo/br/varzenal/project/dog.png" }) {
      childImageSharp {
        gatsbyImageData
      }
    }
    lockerRoomImage: file(
      relativePath: { eq: "omo/br/varzenal/episodes/photo.png" }
    ) {
      childImageSharp {
        gatsbyImageData
      }
    }
    footballer: file(
      relativePath: { eq: "omo/br/varzenal/episodes/soccer.png" }
    ) {
      childImageSharp {
        gatsbyImageData
      }
    }
    blueStar: file(
      relativePath: { eq: "omo/br/varzenal/episodes/blue_star.png" }
    ) {
      childImageSharp {
        gatsbyImageData
      }
    }

    chair: file(relativePath: { eq: "omo/br/varzenal/teams/chair.png" }) {
      childImageSharp {
        gatsbyImageData
      }
    }

    player: file(relativePath: { eq: "omo/br/varzenal/teams/player.png" }) {
      childImageSharp {
        gatsbyImageData
      }
    }

    shield: file(relativePath: { eq: "omo/br/varzenal/teams/shield.png" }) {
      childImageSharp {
        gatsbyImageData
      }
    }

    team: file(relativePath: { eq: "omo/br/varzenal/teams/team.png" }) {
      childImageSharp {
        gatsbyImageData
      }
    }

    grill: file(relativePath: { eq: "omo/br/varzenal/partners/grill.png" }) {
      childImageSharp {
        gatsbyImageData
      }
    }

    crowd: file(relativePath: { eq: "omo/br/varzenal/partners/crowd.png" }) {
      childImageSharp {
        gatsbyImageData
      }
    }
    aclimacao: file(
      relativePath: { eq: "omo/br/varzenal/teams/carousel/Aclimacao.png" }
    ) {
      childImageSharp {
        gatsbyImageData
      }
    }
    embuGuacu: file(
      relativePath: { eq: "omo/br/varzenal/teams/carousel/EmbuGuacu.jpg" }
    ) {
      childImageSharp {
        gatsbyImageData
      }
    }
    guarulhos: file(
      relativePath: { eq: "omo/br/varzenal/teams/carousel/Guarulhos.png" }
    ) {
      childImageSharp {
        gatsbyImageData
      }
    }
    ipiranga: file(
      relativePath: { eq: "omo/br/varzenal/teams/carousel/ipiranga.jpg" }
    ) {
      childImageSharp {
        gatsbyImageData
      }
    }
    jardimSenice: file(
      relativePath: { eq: "omo/br/varzenal/teams/carousel/JardimSenice.jpg" }
    ) {
      childImageSharp {
        gatsbyImageData
      }
    }
    raiz: file(
      relativePath: { eq: "omo/br/varzenal/teams/carousel/Raiz.jpg" }
    ) {
      childImageSharp {
        gatsbyImageData
      }
    }
    tiradentes: file(
      relativePath: { eq: "omo/br/varzenal/teams/carousel/Tiradentes.png" }
    ) {
      childImageSharp {
        gatsbyImageData
      }
    }
    vilaSuica: file(
      relativePath: { eq: "omo/br/varzenal/teams/carousel/VilaSuica.jpg" }
    ) {
      childImageSharp {
        gatsbyImageData
      }
    }
  }
`;

export const Head = ({ data }: Props) => {
  if (!data.website) {
    throw new Error("Could not find website node");
  }
  return (
    <GlobalHead
      absolutePath={href}
      metaDescription={description}
      home={data.website}
      pageTitle={title}
    />
  );
};
